import moment from "moment";

type PacketData = Record<string, [{ ts: string; value: string }]>;
type PopulateLogicType = { divide_by?: number; unit?: string; show_time_on_hover?: boolean };

function renderEmptyText(element: HTMLElement) {
  const textComp = element.querySelector("tspan");
  if (!textComp) return;
  textComp.textContent = "-";
}

function populateText(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  if (!data || !data?.[key] || !data?.[key]?.[0] || !("value" in data?.[key]?.[0])) {
    renderEmptyText(element);
    return;
  }
  const value = data?.[key]?.[0]?.value;

  if (value === "***") {
    renderEmptyText(element);
    return;
  }

  const { divide_by = 1, unit = "", show_time_on_hover } = config;
  const numerical_value = Number(value);
  if (!value) return;
  let divided_value;
  if (numerical_value && !isNaN(numerical_value)) {
    divided_value = numerical_value / divide_by + " " + unit;
  } else {
    divided_value = value;
  }
  const textComp = element.querySelector("tspan");
  if (!textComp) return;
  textComp.textContent = divided_value;

  if (show_time_on_hover) {
    showTimeOnHover(element, data, key, config);
  }
}

function getNumericalValue(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  if (!data || !data?.[key] || !data?.[key]?.[0] || !("value" in data?.[key]?.[0]) || data?.[key]?.[0]?.value === "***")
    return null;

  const value = data?.[key]?.[0]?.value;
  const numerical_value = Number(value);
  if (!value || isNaN(numerical_value)) return null;

  return numerical_value;
}

function populateDateTime(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  const device_key_timestamp = data?.[key]?.[0]?.ts;
  if (!device_key_timestamp || isNaN(Number(device_key_timestamp))) return;
  const textComp = element.querySelector("tspan");
  if (!textComp) return;
  textComp.textContent = moment(Number(device_key_timestamp)).format("DD-MM-YYYY HH:mm:ss");
}

function colorShuntStatusCircles(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  const numerical_value = getNumericalValue(element, data, key, config);
  const ioff_value = getNumericalValue(element, data, "IOFF", config);
  const pilot_value = getNumericalValue(element, data, "IPL", config);
  const ion_value = getNumericalValue(element, data, "ION", config);

  if (ioff_value && !pilot_value && !ion_value) {
    if (key === "ION") {
      element.style.fill = "black";
    } else {
      element.style.fill = "white";
    }
    return;
  }
  if (numerical_value === null) return;
  if (numerical_value > 40) {
    element.style.fill = "white";
  }
}

function showTimeOnHover(element: HTMLElement, data: PacketData, key: string, config: PopulateLogicType) {
  const timestamp = data[key][0].ts;
  if (!timestamp) return;
  const formatted_date_time = moment(Number(timestamp)).format("DD-MM-YYYY HH:mm:ss");
  var titleElement = document.createElementNS("http://www.w3.org/2000/svg", "title");
  titleElement.textContent = `Last Time Captured: ${formatted_date_time}`;
  element.style.cursor = "pointer";
  element.appendChild(titleElement);
}

export function getShuntSignalConfig(device_id: string): Record<string, any> {
  return {
    SH_OFF: {
      key: "IOFF",
      device_id,
      populate_element: colorShuntStatusCircles,
    },
    SH_ON: {
      key: "ION",
      device_id,
      populate_element: colorShuntStatusCircles,
    },
    SH_PILOT: {
      key: "IPL",
      device_id,
      populate_element: colorShuntStatusCircles,
    },
    DATE: {
      key: "DATE",
      device_id,
      populate_element: populateDateTime,
    },
    VON: {
      key: "VON",
      device_id,
      unit: "V",
      divide_by: 1000,
      populate_element: populateText,
    },
    ION: {
      key: "ION",
      device_id,
      unit: "mA",
      populate_element: populateText,
    },
    VOFF: {
      key: "VOFF",
      device_id,
      unit: "V",
      divide_by: 1000,
      populate_element: populateText,
    },
    IOFF: {
      key: "IOFF",
      device_id,
      unit: "mA",
      populate_element: populateText,
    },
    V_PILOT: {
      key: "PL",
      device_id,
      unit: "V",
      divide_by: 1000,
      populate_element: populateText,
    },
    "I-PILOT": {
      key: "IPL",
      device_id,
      unit: "mA",
      populate_element: populateText,
    },
    HPR_VOLTAGE: {
      key: "SHPR",
      device_id,
      unit: "V",
      divide_by: 1000,
      populate_element: populateText,
    },
    GEAR_NAME: {
      key: "NAME",
      device_id,
      populate_element: populateText,
    },
  };
}
